import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import FloatingBubbles from '../components/FloatingBubbles';
import GlassCard from '../components/GlassCard';
import JellyButton from '../components/JellyButton';
import { ChevronLeft, Users, Image as ImageIcon, TrendingUp, Calendar, Plus, Upload, Trash2, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(() => localStorage.getItem('glowbox_admin_auth') === '1');
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [stats, setStats] = useState({
    totalSessions: 0,
    todaySessions: 0,
    avgSessionsPerDay: 0
  });
  const [loading, setLoading] = useState(() => localStorage.getItem('glowbox_admin_auth') === '1');
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [frames, setFrames] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');

  // Form states for new frame
  const [newFrame, setNewFrame] = useState({
    name: '',
    slots: 3,
    layout: 'landscape',
    bgColor: '#f0f4f8'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!isAuthed) return;
    setLoading(true);
    fetchDashboardData();
    fetchFrameData();
  }, [isAuthed]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginUsername === 'glad2glow' && loginPassword === 'kucing123') {
      localStorage.setItem('glowbox_admin_auth', '1');
      setIsAuthed(true);
      setLoginPassword('');
      toast.success('Login berhasil');
      const redirectTo = localStorage.getItem('glowbox_admin_redirect');
      if (redirectTo) {
        localStorage.removeItem('glowbox_admin_redirect');
        navigate(redirectTo);
        return;
      }
      return;
    }
    toast.error('Username atau password salah');
  };

  const handleLogout = () => {
    localStorage.removeItem('glowbox_admin_auth');
    setIsAuthed(false);
    setLoginUsername('');
    setLoginPassword('');
    setActiveTab('stats');
  };

  const openBrowserPrint = (photoData, fitMode = 'contain') => {
    if (!photoData) {
      toast.error('Foto tidak tersedia');
      return;
    }

    const safeFit = fitMode === 'cover' ? 'cover' : 'contain';

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch (e) {}
    };

    iframe.onload = () => {
      const w = iframe.contentWindow;
      if (!w) {
        cleanup();
        toast.error('Gagal memulai proses cetak');
        return;
      }

      const done = () => {
        try { w.removeEventListener('afterprint', done); } catch (e) {}
        cleanup();
      };

      try {
        w.addEventListener('afterprint', done);
      } catch (e) {}

      setTimeout(() => {
        try { w.focus(); } catch (e) {}
        try { w.print(); } catch (e) {}
        setTimeout(done, 2000);
      }, 150);
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      toast.error('Gagal memulai proses cetak');
      return;
    }

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Print GlowBox</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            @page { size: 54mm 80mm; margin: 0; }
            html, body { width: 54mm; height: 80mm; margin: 0; padding: 0; }
            body { display: flex; align-items: stretch; justify-content: stretch; background: white; }
            img { width: 54mm; height: 80mm; object-fit: ${safeFit}; display: block; }
          </style>
        </head>
        <body>
          <img src="${photoData}" />
        </body>
      </html>
    `);
    doc.close();
  };

  const fetchDashboardData = async () => {
    try {
      const galleryRes = await axios.get(`${API}/gallery`).catch(() => ({ data: { photos: [] } }));
      const gallery = Array.isArray(galleryRes.data?.photos) ? galleryRes.data.photos : [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayPhotos = gallery.filter(p => {
        const photoDate = new Date(p.timestamp);
        photoDate.setHours(0, 0, 0, 0);
        return photoDate.getTime() === today.getTime();
      });

      setStats({
        totalSessions: gallery.length,
        todaySessions: todayPhotos.length,
        avgSessionsPerDay: Math.round(gallery.length / 7) || 0
      });

      setRecentPhotos(gallery.slice(0, 10));
      setGalleryPhotos(gallery);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchFrameData = async () => {
    try {
      const framesRes = await axios.get(`${API}/frames`);
      setFrames(Array.isArray(framesRes.data) ? framesRes.data : []);
    } catch (error) {
      console.error('Error fetching frame data:', error);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran file terlalu besar (maks 10MB)');
      setSelectedFile(null);
      return;
    }
    const isPng =
      !!file &&
      (file.type === 'image/png' ||
        (typeof file.name === 'string' && file.name.toLowerCase().endsWith('.png')));
    if (isPng) {
      setSelectedFile(file);
      toast.success('File PNG terpilih!');
    } else {
      toast.error('Mohon upload file PNG');
    }
  };

  const handleUploadFrame = async (e) => {
    e.preventDefault();
    if (!selectedFile || !newFrame.name) {
      toast.error('Isi semua data dan pilih file');
      return;
    }
    if (!Number.isFinite(newFrame.slots) || newFrame.slots <= 0) {
      toast.error('Jumlah slot tidak valid');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', newFrame.name);
    formData.append('slots', String(newFrame.slots));
    formData.append('layout', newFrame.layout);
    formData.append('bgColor', newFrame.bgColor);
    
    // Generate layout based on slots (900x600 canvas)
    let photoLayout = [];
    const margin = 20;
    const canvasWidth = 900;
    const canvasHeight = 600;

    if (newFrame.slots === 1) {
      photoLayout = [{ x: margin, y: margin, width: canvasWidth - margin*2, height: canvasHeight - margin*2 }];
    } else if (newFrame.slots === 2) {
      const w = (canvasWidth - margin * 3) / 2;
      const h = canvasHeight - margin * 2;
      photoLayout = [
        { x: margin, y: margin, width: w, height: h },
        { x: margin * 2 + w, y: margin, width: w, height: h }
      ];
    } else if (newFrame.slots === 3) {
      photoLayout = [
        { x: 25, y: 25, width: 415, height: 260 },
        { x: 460, y: 25, width: 415, height: 260 },
        { x: 25, y: 315, width: 415, height: 260 }
      ];
    } else if (newFrame.slots === 4) {
      const w = (canvasWidth - margin * 3) / 2;
      const h = (canvasHeight - margin * 3) / 2;
      photoLayout = [
        { x: margin, y: margin, width: w, height: h },
        { x: margin * 2 + w, y: margin, width: w, height: h },
        { x: margin, y: margin * 2 + h, width: w, height: h },
        { x: margin * 2 + w, y: margin * 2 + h, width: w, height: h }
      ];
    } else if (newFrame.slots === 6) {
      const w = (canvasWidth - margin * 4) / 3;
      const h = (canvasHeight - margin * 3) / 2;
      for (let r = 0; r < 2; r++) {
        for (let c = 0; c < 3; c++) {
          photoLayout.push({
            x: margin + c * (w + margin),
            y: margin + r * (h + margin),
            width: w,
            height: h
          });
        }
      }
    } else if (newFrame.slots === 8) {
      const w = (canvasWidth - margin * 5) / 4;
      const h = (canvasHeight - margin * 3) / 2;
      for (let r = 0; r < 2; r++) {
        for (let c = 0; c < 4; c++) {
          photoLayout.push({
            x: margin + c * (w + margin),
            y: margin + r * (h + margin),
            width: w,
            height: h
          });
        }
      }
    } else {
      // Default fallback
      photoLayout = Array.from({ length: newFrame.slots }).map((_, i) => ({
        x: 50 + (i * 100), y: 50, width: 80, height: 80
      }));
    }

    formData.append('photoLayout', JSON.stringify(photoLayout));

    try {
      await axios.post(`${API}/frames/upload`, formData);
      toast.success('Frame berhasil diupload!');
      setNewFrame({
        name: '',
        slots: 3,
        layout: 'landscape',
        bgColor: '#f0f4f8'
      });
      setSelectedFile(null);
      fetchFrameData();
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Gagal mengupload frame');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFrame = async (frameId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus frame ini?')) return;

    try {
      await axios.delete(`${API}/frames/${frameId}`);
      toast.success('Frame berhasil dihapus');
      fetchFrameData();
    } catch (error) {
      console.error('Error deleting frame:', error);
      toast.error('Gagal menghapus frame');
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/gallery/${photoId}`);
      setGalleryPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setRecentPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('Foto berhasil dihapus');
    } catch (error) {
      console.error('Error deleting gallery photo:', error);
      toast.error('Gagal menghapus foto');
    }
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="glass-card rounded-3xl p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-2xl bg-white/50`} style={{ backgroundColor: `${color}20` }}>
          <Icon className={`w-6 h-6`} style={{ color }} />
        </div>
        <TrendingUp className="w-5 h-5 text-green-500" />
      </div>
      <h3 className="text-3xl font-bold text-[#592E39] mb-1">{value}</h3>
      <p className="text-[#8B5F6D] font-medium text-sm">{title}</p>
    </motion.div>
  );

  if (!isAuthed) {
    return (
      <div className="candy-gradient-bg min-h-screen flex items-center justify-center px-4 py-10">
        <FloatingBubbles />
        <div className="relative z-10 w-full max-w-md">
          <GlassCard className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#592E39] font-['Fredoka']">
                Admin Login
              </h2>
              <p className="text-sm text-[#8B5F6D] font-medium mt-1">
                Masuk untuk akses dashboard
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#592E39] mb-1">Username</label>
                <input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-white/50 border-2 border-pink-200 focus:border-pink-500 outline-none"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#592E39] mb-1">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-white/50 border-2 border-pink-200 focus:border-pink-500 outline-none"
                  autoComplete="current-password"
                />
              </div>

              <JellyButton type="submit" className="w-full">
                Login
              </JellyButton>

              <JellyButton
                type="button"
                variant="secondary"
                onClick={() => navigate('/')}
                className="w-full"
              >
                Kembali
              </JellyButton>
            </form>
          </GlassCard>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="candy-gradient-bg min-h-screen flex items-center justify-center">
        <GlassCard className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-candy-bright-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#592E39] font-medium text-lg">Memuat dashboard...</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="candy-gradient-bg min-h-screen py-8 px-4 overflow-y-auto">
      <FloatingBubbles />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="p-3 rounded-full bg-white/40 hover:bg-white/80 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-candy-bright-pink" />
          </button>
          
          <h2 className="text-3xl md:text-4xl font-bold text-[#592E39] font-['Fredoka']">
            Admin Dashboard 📊
          </h2>
          
          <div className="flex gap-2 w-full sm:w-auto justify-center sm:justify-end flex-wrap">
            <button 
              onClick={() => setActiveTab('stats')}
              className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-xl font-bold transition-all ${activeTab === 'stats' ? 'bg-pink-500 text-white' : 'bg-white/40 text-pink-600'}`}
            >
              Stats
            </button>
            <button 
              onClick={() => setActiveTab('gallery')}
              className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-xl font-bold transition-all ${activeTab === 'gallery' ? 'bg-pink-500 text-white' : 'bg-white/40 text-pink-600'}`}
            >
              Gallery
            </button>
            <button 
              onClick={() => setActiveTab('frames')}
              className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-xl font-bold transition-all ${activeTab === 'frames' ? 'bg-pink-500 text-white' : 'bg-white/40 text-pink-600'}`}
            >
              Frames
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 text-sm sm:px-4 sm:text-base rounded-xl font-bold transition-all bg-white/40 text-pink-600 hover:bg-white/80"
            >
              Logout
            </button>
          </div>
        </div>

        {activeTab === 'stats' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={Users}
                title="Total Sessions"
                value={stats.totalSessions}
                color="#FF69B4"
              />
              <StatCard
                icon={Calendar}
                title="Hari Ini"
                value={stats.todaySessions}
                color="#FFD700"
              />
              <StatCard
                icon={TrendingUp}
                title="Rata-rata / Hari"
                value={stats.avgSessionsPerDay}
                color="#32CD32"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 mb-8">
              <GlassCard>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-[#592E39] font-['Fredoka']">
                    Recent Photos
                  </h3>
                  <ImageIcon className="w-6 h-6 text-candy-bright-pink" />
                </div>
                
                {recentPhotos.length === 0 ? (
                  <p className="text-[#8B5F6D] text-center py-8">Belum ada foto</p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {recentPhotos.map((photo) => (
                      <div key={photo.id} className="aspect-[3/8] rounded-xl overflow-hidden border-2 border-white/40">
                        <img
                          src={photo.photo_data}
                          alt={photo.frame_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>
            </div>
          </>
        ) : activeTab === 'frames' ? (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Frame Management */}
            <GlassCard>
              <h3 className="text-2xl font-bold text-[#592E39] mb-6 font-['Fredoka'] flex items-center gap-2">
                <Upload className="w-6 h-6" /> Upload Frame Baru
              </h3>
              <form onSubmit={handleUploadFrame} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-[#592E39] mb-1">Nama Frame</label>
                  <input
                    type="text"
                    value={newFrame.name}
                    onChange={(e) => setNewFrame({...newFrame, name: e.target.value})}
                    className="w-full px-4 py-2 rounded-xl bg-white/50 border-2 border-pink-200 focus:border-pink-500 outline-none"
                    placeholder="Contoh: Tropical Summer"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#592E39] mb-1">Jumlah Slot</label>
                    <input
                      type="number"
                      value={newFrame.slots}
                      onChange={(e) => setNewFrame({...newFrame, slots: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 rounded-xl bg-white/50 border-2 border-pink-200 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#592E39] mb-1">BG Color</label>
                    <input
                      type="color"
                      value={newFrame.bgColor}
                      onChange={(e) => setNewFrame({...newFrame, bgColor: e.target.value})}
                      className="w-full h-11 p-1 rounded-xl bg-white/50 border-2 border-pink-200 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#592E39] mb-1">File Overlay (PNG)</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/png"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="frame-upload"
                    />
                    <label
                      htmlFor="frame-upload"
                      className="flex items-center justify-center gap-2 w-full px-4 py-4 rounded-2xl border-2 border-dashed border-pink-300 bg-pink-50/30 hover:bg-pink-50/50 cursor-pointer transition-all"
                    >
                      {selectedFile ? (
                        <span className="text-pink-600 font-bold">{selectedFile.name}</span>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 text-pink-500" />
                          <span className="text-pink-500 font-bold">Pilih File PNG</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                <JellyButton type="submit" disabled={isUploading} className="w-full py-4">
                  {isUploading ? 'Mengupload...' : 'Upload Frame'}
                </JellyButton>
              </form>
            </GlassCard>

            <div className="space-y-6">
              {/* Frames List */}
              <GlassCard>
                <h3 className="text-2xl font-bold text-[#592E39] mb-6 font-['Fredoka']">
                  Daftar Frame ({frames.length})
                </h3>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {frames.map(frame => (
                    <div key={frame.id} className="flex items-center gap-4 bg-white/40 p-3 rounded-2xl border border-white/60">
                      <div className="w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center overflow-hidden">
                        <img src={frame.preview} alt="" className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-[#592E39]">{frame.name}</p>
                        <p className="text-xs text-[#8B5F6D]">{frame.slots} slots</p>
                      </div>
                      <button
                        onClick={() => handleDeleteFrame(frame.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Hapus Frame"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </div>
        ) : (
          <div className="mb-8">
            <GlassCard className="flex flex-col min-h-0 overflow-hidden">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-2xl font-bold text-[#592E39] font-['Fredoka']">
                  Gallery
                </h3>
                <p className="text-sm font-bold text-[#8B5F6D]">
                  Total: {galleryPhotos.length}
                </p>
              </div>

              {galleryPhotos.length === 0 ? (
                <p className="text-[#8B5F6D] text-center py-8">Belum ada foto</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto pr-1 max-h-[70vh]">
                  {galleryPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative aspect-[2/3] rounded-xl overflow-hidden border-2 border-white/40"
                    >
                      <img
                        src={photo.photo_data}
                        alt={photo.frame_name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => openBrowserPrint(photo.photo_data, 'contain')}
                        className="absolute top-2 left-2 bg-white/80 hover:bg-white text-pink-600 rounded-full p-2 transition-all"
                        title="Print"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGalleryPhoto(photo.id)}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white text-red-600 rounded-full p-2 transition-all"
                        title="Hapus"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-bold truncate">{photo.frame_name}</p>
                        <p className="text-white/80 text-[10px]">
                          {new Date(photo.timestamp).toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>
        )}

        <div className="mt-6 text-center">
          <JellyButton onClick={() => navigate('/')} variant="secondary">
            Kembali ke Home
          </JellyButton>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
